'use client';
import React, { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import Modal from '@/components/ui/Modal';

const PATHWAYS = [
  { id: 'pathway-all', label: 'All Subjects', icon: '📚', color: 'bg-primary text-white' },
  { id: 'pathway-quran', label: "Qur\'an Studies", icon: '📖', color: 'bg-primary/10 text-primary' },
  { id: 'pathway-tajweed', label: 'Tajweed', icon: '🎵', color: 'bg-purple/10 text-purple' },
  { id: 'pathway-hifdh', label: 'Hifdh', icon: '🌙', color: 'bg-amber-50 text-amber-700' },
  { id: 'pathway-arabic', label: "Qur\'anic Arabic", icon: '🔤', color: 'bg-sky/10 text-sky' },
  { id: 'pathway-aqidah', label: 'Aqidah', icon: '🌟', color: 'bg-yellow-50 text-yellow-700' },
  { id: 'pathway-seerah', label: 'Seerah', icon: '🐪', color: 'bg-orange-50 text-orange-700' },
  { id: 'pathway-hadith', label: 'Hadith Studies', icon: '📜', color: 'bg-green-50 text-green-700' },
  { id: 'pathway-fiqh', label: 'Fiqh Foundations', icon: '⚖️', color: 'bg-indigo-50 text-indigo-700' },
  { id: 'pathway-civili', label: 'Islamic Civilization', icon: '🕌', color: 'bg-rose-50 text-rose-700' },
  { id: 'pathway-ethics', label: 'Islamic Ethics', icon: '💎', color: 'bg-teal/10 text-teal' },
  { id: 'pathway-comp', label: 'Comparative Religion', icon: '🌍', color: 'bg-blue-50 text-blue-700' },
  { id: 'pathway-finance', label: 'Islamic Finance', icon: '💰', color: 'bg-emerald-50 text-emerald-700' },
  { id: 'pathway-research', label: 'Scholarly Research', icon: '🔬', color: 'bg-violet-50 text-violet-700' },
  { id: 'pathway-adab', label: 'Islamic Adab', icon: '✨', color: 'bg-pink-50 text-pink-700' },
];

const LEVELS = ['All Levels', 'Foundation', 'Elementary', 'Intermediate', 'Advanced', 'Scholar'];
const AGE_GROUPS = ['All Ages', 'Ages 4–7', 'Ages 8–12', 'Ages 13–17', 'Adult', 'Graduate'];

const COURSES = [
  {
    id: 'course-001',
    title: 'Tajweed Mastery: From Alphabet to Recitation',
    pathway: 'Tajweed',
    pathwayId: 'pathway-tajweed',
    level: 'Intermediate',
    ageGroup: 'Ages 8–12',
    educator: 'Sheikh Omar Al-Farouqi',
    lessons: 24,
    duration: '8 weeks',
    enrolled: 142,
    rating: 4.9,
    status: 'enrolled' as const,
    arabicTitle: 'التجويد المتقن',
    description: 'A complete Tajweed course covering all rules of proper Quranic recitation with audio exercises, color-coded tajweed highlighting, and interactive practice.',
    prerequisites: ['Arabic Alphabet Mastery', 'Basic Quran Reading'],
    syllabus: ['Makhaarij al-Huroof', 'Sifaat al-Huroof', 'Noon Saakinah rules', 'Meem Saakinah', 'Madd rules', 'Waqf & Ibtidaa', 'Final assessment'],
    icon: '🎵',
    featured: true,
  },
  {
    id: 'course-002',
    title: "Qur\'anic Arabic: Root Word Exploration",
    pathway: "Qur\'anic Arabic",
    pathwayId: 'pathway-arabic',
    level: 'Elementary',
    ageGroup: 'Ages 8–12',
    educator: 'Dr. Aisha Bint Khalid',
    lessons: 32,
    duration: '12 weeks',
    enrolled: 218,
    rating: 4.8,
    status: 'in-progress' as const,
    arabicTitle: 'الجذور القرآنية',
    description: 'Explore the root-word system of Quranic Arabic, building vocabulary through thematic Surah analysis and daily life connections.',
    prerequisites: ['Arabic Alphabet'],
    syllabus: ['Arabic root system overview', 'Verb forms (أوزان)', 'Noun derivations', 'Surah Al-Fatiha deep dive', 'Surah Al-Baqarah vocabulary', 'Translation exercises'],
    icon: '🔤',
    featured: true,
  },
  {
    id: 'course-003',
    title: 'Hifdh Support Programme: Juz Amma',
    pathway: 'Hifdh',
    pathwayId: 'pathway-hifdh',
    level: 'Foundation',
    ageGroup: 'Ages 4–7',
    educator: 'Ustadha Maryam Hassan',
    lessons: 40,
    duration: '16 weeks',
    enrolled: 387,
    rating: 4.9,
    status: 'locked' as const,
    arabicTitle: 'حفظ جزء عم',
    description: 'A structured Hifdh program for young learners focusing on Juz Amma with memorization tracking, audio support, and daily review schedules.',
    prerequisites: ['Basic Arabic letters'],
    syllabus: ['Surah Al-Fatiha', 'Surah Al-Nas to Al-Duha', 'Daily revision system', 'Milestone assessments', 'Parent progress reports'],
    icon: '🌙',
    featured: true,
  },
  {
    id: 'course-004',
    title: 'Seerah Adventures: The Life of the Prophet ﷺ',
    pathway: 'Seerah',
    pathwayId: 'pathway-seerah',
    level: 'Elementary',
    ageGroup: 'Ages 8–12',
    educator: 'Ustadh Bilal Yusuf',
    lessons: 28,
    duration: '10 weeks',
    enrolled: 295,
    rating: 4.7,
    status: 'enrolled' as const,
    arabicTitle: 'رحلة السيرة',
    description: 'Interactive storytelling journey through the life of Prophet Muhammad ﷺ with illustrated chapters, quizzes, and character reflection activities.',
    prerequisites: [],
    syllabus: ['Arabia before Islam', 'Birth and childhood', 'First revelation', 'Makkan period', 'Hijra', 'Madinan period', 'Final years'],
    icon: '🐪',
    featured: false,
  },
  {
    id: 'course-005',
    title: 'Fiqh Foundations: Purification & Prayer',
    pathway: 'Fiqh Foundations',
    pathwayId: 'pathway-fiqh',
    level: 'Foundation',
    ageGroup: 'Ages 8–12',
    educator: 'Sheikh Hamza Al-Rashid',
    lessons: 20,
    duration: '7 weeks',
    enrolled: 176,
    rating: 4.6,
    status: 'locked' as const,
    arabicTitle: 'فقه الطهارة والصلاة',
    description: 'Essential Fiqh covering the conditions and obligations of Wudu, Ghusl, and the five daily prayers with practical demonstrations.',
    prerequisites: ['Basic Aqidah'],
    syllabus: ['Types of water', 'Wudu — conditions & obligations', 'Ghusl', 'Prayer times', 'Prayer conditions', 'Prayer pillars', 'Common invalidators'],
    icon: '💧',
    featured: false,
  },
  {
    id: 'course-006',
    title: 'Aqidah Foundations: The Six Pillars of Iman',
    pathway: 'Aqidah',
    pathwayId: 'pathway-aqidah',
    level: 'Elementary',
    ageGroup: 'Ages 8–12',
    educator: 'Dr. Khalid Al-Mansouri',
    lessons: 18,
    duration: '6 weeks',
    enrolled: 224,
    rating: 4.8,
    status: 'in-progress' as const,
    arabicTitle: 'أركان الإيمان الستة',
    description: 'A foundational Aqidah course exploring the six pillars of Iman with age-appropriate explanations, stories, and reflection exercises.',
    prerequisites: [],
    syllabus: ['Iman in Allah', 'Angels', 'Divine Books', 'Prophets & Messengers', 'Day of Judgment', 'Divine Decree'],
    icon: '🌟',
    featured: false,
  },
  {
    id: 'course-007',
    title: 'Islamic Civilization: The Golden Age',
    pathway: 'Islamic Civilization',
    pathwayId: 'pathway-civili',
    level: 'Intermediate',
    ageGroup: 'Ages 13–17',
    educator: 'Prof. Fatima Al-Zahrawi',
    lessons: 22,
    duration: '8 weeks',
    enrolled: 134,
    rating: 4.5,
    status: 'locked' as const,
    arabicTitle: 'الحضارة الإسلامية',
    description: 'An exploration of Islamic civilization from the Abbasid Golden Age through contributions in science, medicine, philosophy, and architecture.',
    prerequisites: ['Basic Seerah knowledge'],
    syllabus: ['The Abbasid Caliphate', 'House of Wisdom', 'Islamic medicine', 'Mathematics & astronomy', 'Architecture', 'Trade routes', 'Legacy today'],
    icon: '🕌',
    featured: false,
  },
  {
    id: 'course-008',
    title: 'Usul al-Fiqh: Principles of Islamic Jurisprudence',
    pathway: 'Scholarly Research',
    pathwayId: 'pathway-research',
    level: 'Scholar',
    ageGroup: 'Graduate',
    educator: 'Sheikh Dr. Yusuf Al-Qaradawi Jr.',
    lessons: 48,
    duration: '20 weeks',
    enrolled: 67,
    rating: 4.9,
    status: 'locked' as const,
    arabicTitle: 'أصول الفقه',
    description: 'Advanced scholarly pathway covering the foundational principles of Islamic jurisprudence, Maqasid al-Shariah, and contemporary application.',
    prerequisites: ['Fiqh Foundations', 'Arabic Grammar Advanced', 'Hadith Sciences'],
    syllabus: ["Hukm shar\'i", 'Sources of Fiqh', 'Qiyas methodology', 'Ijma', 'Maslaha', 'Maqasid al-Shariah', 'Contemporary issues'],
    icon: '🔬',
    featured: false,
  },
  {
    id: 'course-009',
    title: 'Hadith Sciences: Introduction to Mustalah',
    pathway: 'Hadith Studies',
    pathwayId: 'pathway-hadith',
    level: 'Advanced',
    ageGroup: 'Adult',
    educator: 'Ustadh Nuh Keller Al-Azhari',
    lessons: 36,
    duration: '14 weeks',
    enrolled: 89,
    rating: 4.7,
    status: 'locked' as const,
    arabicTitle: 'مصطلح الحديث',
    description: 'A rigorous introduction to Hadith terminology, chain of narration analysis, and the classification of Hadith by authenticity.',
    prerequisites: ['Quran Reading', 'Basic Arabic'],
    syllabus: ['Definition of Hadith', 'Isnad & Matn', 'Categories of Hadith', 'Rijal al-Hadith', 'The six books', 'Authentication methodology'],
    icon: '📜',
    featured: false,
  },
  {
    id: 'course-010',
    title: 'Islamic Ethics: Character & Service',
    pathway: 'Islamic Ethics',
    pathwayId: 'pathway-ethics',
    level: 'Elementary',
    ageGroup: 'Ages 8–12',
    educator: 'Ustadha Zainab Nour',
    lessons: 16,
    duration: '6 weeks',
    enrolled: 312,
    rating: 4.8,
    status: 'enrolled' as const,
    arabicTitle: 'الأخلاق الإسلامية',
    description: 'An engaging ethics course building Islamic character through storytelling, ethical decision scenarios, and community service missions.',
    prerequisites: [],
    syllabus: ['Honesty (Sidq)', 'Patience (Sabr)', 'Gratitude (Shukr)', 'Generosity (Karam)', 'Service to community', 'Adab with parents', 'Final project'],
    icon: '💎',
    featured: false,
  },
  {
    id: 'course-011',
    title: 'Arabic Alphabet & Phonics: Early Childhood',
    pathway: "Qur\'anic Arabic",
    pathwayId: 'pathway-arabic',
    level: 'Foundation',
    ageGroup: 'Ages 4–7',
    educator: 'Ustadha Halima Yusuf',
    lessons: 26,
    duration: '10 weeks',
    enrolled: 445,
    rating: 4.9,
    status: 'locked' as const,
    arabicTitle: 'الحروف العربية للأطفال',
    description: 'A joyful introduction to Arabic letters for young children using songs, illustrated characters, tracing exercises, and phonics games.',
    prerequisites: [],
    syllabus: ['Letters Alif–Jeem', 'Letters Haa–Khaa', 'Letters Daal–Zay', 'Letters Seen–Dhaad', 'Short vowels', 'Long vowels', 'Simple words'],
    icon: '🌈',
    featured: false,
  },
  {
    id: 'course-012',
    title: 'Islamic Finance: Principles & Practice',
    pathway: 'Islamic Finance',
    pathwayId: 'pathway-finance',
    level: 'Advanced',
    ageGroup: 'Adult',
    educator: 'Dr. Musa Al-Tamimi',
    lessons: 30,
    duration: '12 weeks',
    enrolled: 98,
    rating: 4.6,
    status: 'locked' as const,
    arabicTitle: 'التمويل الإسلامي',
    description: 'A comprehensive course on Islamic finance principles including prohibition of Riba, Murabaha, Musharaka, Sukuk, and ethical investment frameworks.',
    prerequisites: ['Fiqh Foundations'],
    syllabus: ['Prohibition of Riba', 'Halal investment', 'Murabaha', 'Ijara', 'Musharaka', 'Sukuk', 'Zakat calculation'],
    icon: '💰',
    featured: false,
  },
];

export default function CourseExplorerClient() {
  const [selectedPathway, setSelectedPathway] = useState('pathway-all');
  const [selectedLevel, setSelectedLevel] = useState('All Levels');
  const [selectedAge, setSelectedAge] = useState('All Ages');
  const [search, setSearch] = useState('');
  const [enrollModal, setEnrollModal] = useState<typeof COURSES[0] | null>(null);

  const filtered = useMemo(() => {
    return COURSES.filter((c) => {
      const matchPathway = selectedPathway === 'pathway-all' || c.pathwayId === selectedPathway;
      const matchLevel = selectedLevel === 'All Levels' || c.level === selectedLevel;
      const matchAge = selectedAge === 'All Ages' || c.ageGroup === selectedAge;
      const matchSearch = !search || c.title.toLowerCase().includes(search.toLowerCase()) || c.pathway.toLowerCase().includes(search.toLowerCase());
      return matchPathway && matchLevel && matchAge && matchSearch;
    });
  }, [selectedPathway, selectedLevel, selectedAge, search]);

  const featured = COURSES.filter((c) => c.featured);

  return (
    <div className="space-y-6">
      {/* Featured Banner */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {featured.map((course) => (
          <div
            key={`featured-${course.id}`}
            className="rounded-2xl overflow-hidden border border-border bg-card card-hover cursor-pointer"
            onClick={() => setEnrollModal(course)}
          >
            <div className="gradient-green p-5 text-white">
              <span className="text-3xl">{course.icon}</span>
              <p className="text-xs font-600 text-green-200 mt-2 uppercase tracking-wide">{course.pathway}</p>
              <h3 className="text-sm font-700 mt-1 leading-snug">{course.title}</h3>
              <p className="text-xs text-green-100 mt-1 rtl-text">{course.arabicTitle}</p>
            </div>
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground">{course.lessons} lessons</span>
                <span className="text-xs text-muted-foreground">{course.duration}</span>
              </div>
              <span className="text-xs font-700 text-amber-600">⭐ {course.rating}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Search + Filters */}
      <div className="bg-card rounded-2xl border border-border p-5">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search courses, subjects, educators..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="px-4 py-2.5 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
          >
            {LEVELS.map((l) => <option key={`level-${l}`}>{l}</option>)}
          </select>
          <select
            value={selectedAge}
            onChange={(e) => setSelectedAge(e.target.value)}
            className="px-4 py-2.5 bg-muted border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 text-foreground"
          >
            {AGE_GROUPS.map((a) => <option key={`age-${a}`}>{a}</option>)}
          </select>
        </div>

        {/* Pathway chips */}
        <div className="flex flex-wrap gap-2 mt-4">
          {PATHWAYS.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedPathway(p.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-600 border transition-all duration-150 active:scale-95
                ${selectedPathway === p.id
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-muted border-border text-muted-foreground hover:border-primary/40 hover:text-primary'
                }
              `}
            >
              <span>{p.icon}</span>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Showing <span className="font-700 text-foreground">{filtered.length}</span> courses
        </p>
        <select className="text-xs border border-border rounded-lg px-3 py-1.5 bg-muted text-foreground focus:outline-none">
          <option>Most Popular</option>
          <option>Newest</option>
          <option>Highest Rated</option>
          <option>Shortest Duration</option>
        </select>
      </div>

      {/* Course grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <span className="text-5xl mb-4">📚</span>
          <h3 className="text-lg font-600 text-foreground mb-2">No courses found</h3>
          <p className="text-sm text-muted-foreground">Try adjusting your filters or search term.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {filtered.map((course) => (
            <div
              key={course.id}
              className="bg-card rounded-2xl border border-border overflow-hidden card-hover cursor-pointer group"
              onClick={() => setEnrollModal(course)}
            >
              <div className="p-5 border-b border-border">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-3xl">{course.icon}</span>
                  <StatusBadge variant={course.status} label={course.status.replace('-', ' ')} />
                </div>
                <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1">{course.pathway}</p>
                <h3 className="text-sm font-700 text-foreground leading-snug mb-1 group-hover:text-primary transition-colors">{course.title}</h3>
                <p className="text-xs text-muted-foreground rtl-text">{course.arabicTitle}</p>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">{course.level}</span>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-500">{course.ageGroup}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">{course.description}</p>
                <div className="flex items-center justify-between pt-1">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="BookOpenIcon" size={12} />
                      {course.lessons} lessons
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="ClockIcon" size={12} />
                      {course.duration}
                    </span>
                  </div>
                  <span className="text-xs font-700 text-amber-600">⭐ {course.rating}</span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-[10px] font-700 flex-shrink-0">
                    {course.educator.split(' ').pop()![0]}
                  </div>
                  <span className="text-xs text-muted-foreground truncate">{course.educator}</span>
                </div>
                <button
                  className={`w-full py-2 rounded-xl text-xs font-600 transition-colors active:scale-95
                    ${course.status === 'enrolled' || course.status === 'in-progress' ?'bg-primary/10 text-primary hover:bg-primary/20' :'bg-primary text-white hover:bg-primary/90'
                    }
                  `}
                  onClick={(e) => { e.stopPropagation(); setEnrollModal(course); }}
                >
                  {course.status === 'enrolled' ? 'View Course' : course.status === 'in-progress' ? 'Continue' : 'Enroll Now'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Enrollment Modal */}
      {enrollModal && (
        <Modal open={!!enrollModal} onClose={() => setEnrollModal(null)} title={enrollModal.title} size="lg">
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <span className="text-5xl">{enrollModal.icon}</span>
              <div>
                <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide">{enrollModal.pathway}</p>
                <h3 className="text-lg font-700 text-foreground">{enrollModal.title}</h3>
                <p className="text-sm text-muted-foreground rtl-text mt-1">{enrollModal.arabicTitle}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-500">{enrollModal.level}</span>
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-500">{enrollModal.ageGroup}</span>
                  <span className="text-xs text-amber-600 font-700">⭐ {enrollModal.rating}</span>
                  <span className="text-xs text-muted-foreground">{enrollModal.enrolled} enrolled</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">{enrollModal.description}</p>

            <div className="grid grid-cols-3 gap-3">
              <div className="bg-muted rounded-xl p-3 text-center">
                <p className="text-lg font-700 text-foreground">{enrollModal.lessons}</p>
                <p className="text-xs text-muted-foreground">Lessons</p>
              </div>
              <div className="bg-muted rounded-xl p-3 text-center">
                <p className="text-lg font-700 text-foreground">{enrollModal.duration}</p>
                <p className="text-xs text-muted-foreground">Duration</p>
              </div>
              <div className="bg-muted rounded-xl p-3 text-center">
                <p className="text-sm font-700 text-foreground truncate">{enrollModal.educator.split(' ').slice(-2).join(' ')}</p>
                <p className="text-xs text-muted-foreground">Educator</p>
              </div>
            </div>

            {enrollModal.prerequisites.length > 0 && (
              <div>
                <p className="text-sm font-600 text-foreground mb-2">Prerequisites</p>
                <div className="flex flex-wrap gap-2">
                  {enrollModal.prerequisites.map((p) => (
                    <span key={`prereq-${p}`} className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded-lg">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <p className="text-sm font-600 text-foreground mb-2">Syllabus Overview</p>
              <div className="space-y-1">
                {enrollModal.syllabus.map((item, idx) => (
                  <div key={`syllabus-${enrollModal.id}-${idx}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[10px] font-700 flex items-center justify-center flex-shrink-0">
                      {idx + 1}
                    </span>
                    {item}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setEnrollModal(null)}
                className="flex-1 py-2.5 border border-border rounded-xl text-sm font-600 text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setEnrollModal(null)}
                className="flex-1 py-2.5 bg-primary text-white rounded-xl text-sm font-600 hover:bg-primary/90 transition-colors active:scale-95"
              >
                {enrollModal.status === 'enrolled' || enrollModal.status === 'in-progress' ? 'Go to Course' : 'Enroll in Course'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}